import { BASE_URL, RegionCode } from 'shared/src/types/region.type';
import { isAppHostname } from './is-native-app';

/**
 * Cross-region URL resolution that is aware of the native-app origin.
 *
 * On the public web, each region lives on its own apex (BASE_URL) and
 * switching locale means switching domain. Inside the app, every locale is
 * served from the single app host — following an absolute BASE_URL link
 * would eject the user from the app origin and, on iOS, into a different
 * cookie jar (instant logout). Navigation must therefore stay relative on
 * the app host, while anything the user shares or copies must ALWAYS point
 * at the public canonical apex (the app host is noindexed and app-only).
 */

/**
 * For NAVIGATION: same-origin path when running on the app host, absolute
 * public-apex URL otherwise. `path` must start with '/'.
 */
export function resolveCrossRegionUrl(path: string, regionCode: RegionCode | null | undefined): string {
  if (typeof window !== 'undefined' && isAppHostname(window.location.hostname)) {
    return path;
  }
  const base = regionCode ? BASE_URL[regionCode] : undefined;
  return base ? `${base}${path}` : path;
}

/**
 * For SHARE / COPY / OG: always the public canonical origin, never the app
 * host and never window.location.origin blindly. `path` must start with '/'.
 */
export function publicCanonicalUrl(path: string, regionCode: RegionCode | null | undefined): string {
  const base = regionCode ? BASE_URL[regionCode] : undefined;
  if (base) return `${base}${path}`;
  // No region known: the current origin is fine on the public web, but on the
  // app host fall back to the default (CA) apex rather than leaking app URLs.
  if (typeof window !== 'undefined' && !isAppHostname(window.location.hostname)) {
    return `${window.location.origin}${path}`;
  }
  return `${BASE_URL[RegionCode.Canada]}${path}`;
}
